import { Component, Input, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { ApiJobLog, InvoicesService } from 'src/app/services/invoices.service';
import {
  TableSettings,
  TableSpacing,
} from 'src/app/components/pagination/pagination.component';

@Component({
  selector: 'app-api-job-logs',
  templateUrl: './api-job-logs.component.html',
  styleUrl: './api-job-logs.component.scss',
})
export class ApiJobLogsComponent implements OnInit {
  @Input() tenantId?: string;

  loadingParams = false;
  allJobLogs: ApiJobLog[] = []; // All job logs from API
  filteredJobLogs: ApiJobLog[] = []; // Filtered  job logs for display
  paginatedJobLogs: ApiJobLog[] = []; // Paginated job logs for display
  loadingJobLogs = false;
  errorFetchingJobLogs = '';

  // Pagination
  pageSize = 20;
  currentPage = 1;
  totalPages = 1;
  total = 0;
  tableSpacing: TableSpacing = 'normal';

  // Filters
  startDateFilter?: string;
  endDateFilter?: string;
  chargedFilter?: string;

  // Export selection
  selectedJobLogIds = new Set<number>();

  constructor(
    private invoicesService: InvoicesService,
    private location: Location,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.loadingParams = true;

    // Subscribe to query params
    this.route.queryParams.subscribe((params) => {
      this.loadingParams = false;

      // Load settings from localStorage
      this.loadSettings();

      if (!this.tenantId) {
        this.errorFetchingJobLogs = 'Missing tenant ID';
        return;
      }

      // Handle backward compatibility: convert old month (YYYYMM) format to date range
      if (params['month'] && params['month'].length === 6) {
        const monthParam = params['month'];
        // Validate it's all digits
        if (/^\d{6}$/.test(monthParam)) {
          const year = parseInt(monthParam.slice(0, 4), 10);
          const month = parseInt(monthParam.slice(4, 6), 10);
          // Create start date (first day of month)
          const startDate = new Date(year, month - 1, 1);
          // Create end date (last day of month)
          const endDate = new Date(year, month, 0);
          // Format as YYYY-MM-DD for date inputs
          this.startDateFilter = this.formatDateForInput(startDate);
          this.endDateFilter = this.formatDateForInput(endDate);
        } else {
          this.startDateFilter = params['start_date'] || undefined;
          this.endDateFilter = params['end_date'] || undefined;
        }
      } else {
        this.startDateFilter = params['start_date'] || undefined;
        this.endDateFilter = params['end_date'] || undefined;
      }

      this.chargedFilter =
        params['charged'] === 'true' || params['charged'] === 'false'
          ? params['charged']
          : undefined;

      if (!this.startDateFilter && !this.endDateFilter) {
        // set filters to start/end of last month if not provided
        const endDate = new Date();
        endDate.setHours(0, 0, 0, 0);
        endDate.setDate(0);
        this.endDateFilter = this.formatDateForInput(endDate);

        const startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        startDate.setDate(1);
        startDate.setMonth(startDate.getMonth() - 1);
        this.startDateFilter = this.formatDateForInput(startDate);
      }

      this.getJobLogs();
    });
  }

  loadSettings() {
    const settings = localStorage.getItem('jobLogsTableSettings');
    try {
      if (settings) {
        const parsedSettings = JSON.parse(settings);
        this.tableSpacing = parsedSettings.spacing || 'normal';
        if (parsedSettings.pageSize) {
          this.pageSize = parsedSettings.pageSize;
        }
      }
    } catch (err) {
      console.error(err);
      this.tableSpacing = 'normal';
      this.pageSize = 20;
    }
  }

  getJobLogs() {
    if (!this.tenantId) {
      return;
    }

    this.loadingJobLogs = true;
    this.errorFetchingJobLogs = '';

    // Fetch all job logs for tenant (no filters, no pagination)
    this.invoicesService.getApiJobLogsForTenant(this.tenantId).subscribe({
      next: (res) => {
        console.log('Job logs for tenant', res);
        this.loadingJobLogs = false;
        this.allJobLogs = res || [];
        this.applyFiltersAndPagination();
      },
      error: (err) => {
        this.loadingJobLogs = false;
        console.error(err);
        this.errorFetchingJobLogs =
          err.error?.error || 'Failed to fetch job logs';
        this.allJobLogs = [];
        this.filteredJobLogs = [];
        this.paginatedJobLogs = [];
        this.total = 0;
        this.totalPages = 1;
      },
    });
  }

  applyFiltersAndPagination() {
    // Apply client-side filtering
    let filtered = [...this.allJobLogs];

    // Date range filter
    if (this.startDateFilter || this.endDateFilter) {
      filtered = filtered.filter((log) => {
        if (!log.transaction_date) return false;
        const logDate = new Date(log.transaction_date);
        logDate.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

        if (this.startDateFilter) {
          const startDate = new Date(this.startDateFilter);
          startDate.setHours(0, 0, 0, 0);
          if (logDate < startDate) return false;
        }

        if (this.endDateFilter) {
          const endDate = new Date(this.endDateFilter);
          endDate.setHours(23, 59, 59, 999); // Set to end of day for comparison
          if (logDate > endDate) return false;
        }

        return true;
      });
    }

    // Charged filter
    if (this.chargedFilter !== undefined) {
      const charged = this.chargedFilter === 'true';
      filtered = filtered.filter((log) => log.charged === charged);
    }

    // Update total
    this.total = filtered.length;
    this.totalPages = Math.ceil(this.total / this.pageSize);

    this.filteredJobLogs = filtered;
    // Apply pagination
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedJobLogs = this.filteredJobLogs.slice(startIndex, endIndex);
  }

  updateQueryParams() {
    const queryParams: any = {};

    if (this.startDateFilter) {
      queryParams.start_date = this.startDateFilter;
    }
    if (this.endDateFilter) {
      queryParams.end_date = this.endDateFilter;
    }
    if (this.chargedFilter === 'true' || this.chargedFilter === 'false') {
      queryParams.charged = this.chargedFilter;
    } else {
      this.chargedFilter = undefined;
      delete queryParams.charged;
    }

    // create a query params string from the queryParams object
    const urlSearchParams = new URLSearchParams(queryParams);

    this.location.go(window.location.pathname, urlSearchParams.toString());
    // Apply client-side filtering and pagination (no need to refetch from API)
    this.applyFiltersAndPagination();
  }

  applyFilters() {
    this.currentPage = 1;
    this.updateQueryParams();
  }

  clearFilters() {
    this.startDateFilter = undefined;
    this.endDateFilter = undefined;
    this.chargedFilter = undefined;
    this.currentPage = 1;
    this.updateQueryParams();
  }

  changePage(page: number) {
    this.currentPage = page;
    // Only update pagination, no need to refetch from API
    this.applyFiltersAndPagination();
  }

  handleSettingsChanged(settings: TableSettings) {
    this.tableSpacing = settings.spacing;
    this.pageSize = Number(settings.pageSize);
    this.currentPage = 1;
    // Only update pagination, no need to refetch from API
    this.applyFiltersAndPagination();
    const newSettings = {
      pageSize: Number(settings.pageSize),
      spacing: settings.spacing,
    };
    localStorage.setItem('jobLogsTableSettings', JSON.stringify(newSettings));
  }

  formatDate(dateString?: string): string {
    if (!dateString) {
      return 'N/A';
    }
    const date = new Date(dateString);
    return (
      String(date.getUTCMonth() + 1).padStart(2, '0') +
      '/' +
      String(date.getUTCDate()).padStart(2, '0') +
      '/' +
      date.getUTCFullYear()
    );
  }

  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getMaxDisplayIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.total);
  }

  toggleJobLogSelection(jobLogId: number) {
    if (this.selectedJobLogIds.has(jobLogId)) {
      this.selectedJobLogIds.delete(jobLogId);
    } else {
      this.selectedJobLogIds.add(jobLogId);
    }
  }

  isJobLogSelected(jobLogId: number): boolean {
    return this.selectedJobLogIds.has(jobLogId);
  }

  clearCheckedSelections() {
    this.selectedJobLogIds.clear();
  }

  getExportCount(): number {
    // Count all filtered jobs, not just the current page
    let filtered = [...this.filteredJobLogs];

    // If any logs are selected, only count those; otherwise count all filtered logs
    if (this.selectedJobLogIds.size > 0) {
      filtered = filtered.filter((log) => this.selectedJobLogIds.has(log.id));
    }

    return filtered.length;
  }

  exportJobLogs() {
    // Export all filtered logs, not just the current page
    const csv: string[][] = [];
    csv.push([
      'Date',
      'Job GUID',
      'Filename',
      'Record Count',
      'Amount',
      'Charged',
    ]);

    // Apply same filters as display
    let filtered = [...this.filteredJobLogs];

    // If any logs are selected, only export those; otherwise export all filtered logs
    if (this.selectedJobLogIds.size > 0) {
      filtered = filtered.filter((log) => this.selectedJobLogIds.has(log.id));
    }

    for (const jobLog of filtered) {
      csv.push([
        this.formatDate(jobLog.transaction_date),
        jobLog.job_guid,
        jobLog.filename,
        jobLog.record_count?.toString() || '0',
        jobLog.transaction_amount.toFixed(2),
        jobLog.charged ? 'Yes' : 'No',
      ]);
    }
    const csvString = csv.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('style', 'display: none');
    a.setAttribute('target', '_self');
    a.download = 'api-job-logs.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}
